import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter,
  Button,
  Input,
  Select,
  Checkbox,
  FormField,
  DeleteConfirmationModal,
  type SelectOption,
} from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { useTickets } from '@/modules/helpdesk/tickets/hooks';
import {
  useAddProjectTask,
  useUpdateProjectTask,
  useLinkProjectTicket,
  useRemoveProjectTask,
} from '../hooks';
import type {
  DependencyMode,
  ProjectGanttTask,
  ProjectPhase,
  CreateProjectTaskRequest,
  UpdateProjectTaskRequest,
  LinkTicketRequest,
  ProjectDependency,
} from '../types';
import { MODE_OPTIONS, dateInputToIso, isoToDateInput, isTaskDone } from './workspaceHelpers';

export type TaskModalMode = 'create' | 'edit' | 'link';

export interface TaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: TaskModalMode;
  projectId: string;
  /** Pipeline dos tickets do projeto (busca de tickets no modo `link`). */
  pipelineId: string;
  phases: ProjectPhase[];
  /** Todas as tarefas do projeto (opções de dependência + exclusão de já vinculados). */
  allTasks: ProjectGanttTask[];
  /** Tarefa em edição (modo `edit`). */
  task?: ProjectGanttTask | null;
}

const today = (): string => new Date().toISOString().slice(0, 10);

interface DepRow {
  taskId: string;
  mode: DependencyMode;
}

/**
 * Modal de tarefa (Task 13, protótipo G10) — cria tarefa nova, edita cronograma
 * de tarefa existente, ou vincula um ticket já existente. O título vive no
 * ticket (somente-leitura na edição).
 */
export const TaskModal: React.FC<TaskModalProps> = ({
  isOpen,
  onClose,
  mode,
  projectId,
  pipelineId,
  phases,
  allTasks,
  task,
}) => {
  const addTask = useAddProjectTask();
  const updateTask = useUpdateProjectTask();
  const linkTicket = useLinkProjectTicket();
  const removeTask = useRemoveProjectTask();

  const [title, setTitle] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [ticketId, setTicketId] = useState('');
  const [ticketSearch, setTicketSearch] = useState('');
  const [phaseId, setPhaseId] = useState('');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [isMilestone, setIsMilestone] = useState(false);
  const [deps, setDeps] = useState<DepRow[]>([]);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data: users } = useUsersDropdown();
  // Busca de tickets do pipeline apenas no modo link e com o modal aberto.
  const { data: ticketsData } = useTickets(
    { page: 1, limit: 100, filters: { pipelineId } },
    { enabled: isOpen && mode === 'link' && !!pipelineId },
  );

  // Seed do estado ao abrir.
  useEffect(() => {
    if (!isOpen) return;
    if (mode === 'edit' && task) {
      setPhaseId(task.phaseId ?? '');
      setStartDate(isoToDateInput(task.startDate) || today());
      setEndDate(isoToDateInput(task.endDate) || today());
      setIsMilestone(task.isMilestone);
      setDeps((task.dependencies ?? []).map((d) => ({ taskId: d.taskId, mode: d.mode })));
    } else {
      setTitle('');
      setAssigneeId('');
      setTicketId('');
      setTicketSearch('');
      setPhaseId(phases[0]?.id ?? '');
      setStartDate(today());
      setEndDate(today());
      setIsMilestone(false);
      setDeps([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode, task?.id]);

  const phaseOptions = useMemo<SelectOption[]>(
    () => [{ value: '', label: 'Sem fase' }, ...phases.map((p) => ({ value: p.id, label: p.name }))],
    [phases],
  );

  const userOptions = useMemo<SelectOption[]>(
    () => [{ value: '', label: 'Sem responsável' }, ...(users ?? []).map((u) => ({ value: u.id, label: u.name }))],
    [users],
  );

  // Opções de dependência: outras tarefas (exclui a própria em edição).
  const depOptions = useMemo<SelectOption[]>(
    () =>
      allTasks
        .filter((t) => t.id !== task?.id)
        .map((t) => ({ value: t.id, label: t.ticket?.title || 'Tarefa' })),
    [allTasks, task?.id],
  );

  const taskById = useMemo(() => {
    const map = new Map<string, ProjectGanttTask>();
    allTasks.forEach((t) => map.set(t.id, t));
    return map;
  }, [allTasks]);

  // Tickets disponíveis para vincular: do pipeline, ainda não vinculados.
  const ticketOptions = useMemo<SelectOption[]>(() => {
    const linked = new Set(allTasks.map((t) => t.ticket?.ticketId).filter(Boolean) as string[]);
    const term = ticketSearch.trim().toLowerCase();
    return (ticketsData?.items ?? [])
      .filter((t) => !linked.has(t.id))
      .filter((t) =>
        term ? `${t.ticketNumber} ${t.title}`.toLowerCase().includes(term) : true,
      )
      .slice(0, 100)
      .map((t) => ({ value: t.id, label: `#${t.ticketNumber} — ${t.title}` }));
  }, [ticketsData, allTasks, ticketSearch]);

  // Predecessoras pendentes em modo warn/block (warnbox).
  const pendingPredecessors = useMemo(() => {
    return deps
      .filter((d) => d.taskId && (d.mode === 'warn' || d.mode === 'block'))
      .map((d) => taskById.get(d.taskId))
      .filter((t): t is ProjectGanttTask => !!t && !isTaskDone(t));
  }, [deps, taskById]);

  const setStart = (v: string) => {
    setStartDate(v);
    if (isMilestone) setEndDate(v);
  };

  const toggleMilestone = (checked: boolean) => {
    setIsMilestone(checked);
    if (checked) setEndDate(startDate);
  };

  const updateDep = (index: number, patch: Partial<DepRow>) => {
    setDeps((prev) => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)));
  };
  const removeDep = (index: number) => setDeps((prev) => prev.filter((_, i) => i !== index));
  const addDep = () => setDeps((prev) => [...prev, { taskId: '', mode: 'inform' }]);

  const cleanDeps = (): ProjectDependency[] =>
    deps.filter((d) => d.taskId).map((d) => ({ taskId: d.taskId, mode: d.mode }));

  const titleForHeader =
    mode === 'create' ? 'Nova tarefa' : mode === 'link' ? 'Vincular ticket' : task?.ticket?.title || 'Editar tarefa';

  const canSubmit = (() => {
    if (!startDate) return false;
    if (!isMilestone && endDate < startDate) return false;
    if (mode === 'create') return title.trim().length > 0;
    if (mode === 'link') return !!ticketId;
    return true; // edit
  })();

  const isSaving = addTask.isPending || updateTask.isPending || linkTicket.isPending;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const endIso = dateInputToIso(isMilestone ? startDate : endDate);
    const startIso = dateInputToIso(startDate);
    const depList = cleanDeps();

    try {
      if (mode === 'create') {
        const data: CreateProjectTaskRequest = {
          title: title.trim(),
          startDate: startIso,
          endDate: endIso,
          isMilestone,
        };
        if (phaseId) data.phaseId = phaseId;
        if (assigneeId) data.assigneeId = assigneeId;
        if (depList.length > 0) data.dependencies = depList;
        await addTask.mutateAsync({ projectId, data });
      } else if (mode === 'link') {
        const data: LinkTicketRequest = {
          ticketId,
          startDate: startIso,
          endDate: endIso,
          isMilestone,
        };
        if (phaseId) data.phaseId = phaseId;
        if (depList.length > 0) data.dependencies = depList;
        await linkTicket.mutateAsync({ projectId, data });
      } else if (task) {
        const data: UpdateProjectTaskRequest = {
          startDate: startIso,
          endDate: endIso,
          isMilestone,
          dependencies: depList,
        };
        if (phaseId) {
          data.phaseId = phaseId;
        } else if (task.phaseId) {
          // "Sem fase" selecionado numa task que TINHA fase → limpar via $unset no
          // backend. O Zod (`updateProjectTaskSchema`) aceita `null`, mas
          // `UpdateProjectTaskRequest.phaseId` (@troia-v3/types) tipa só
          // `string | undefined` — mesmo boundary-cast do projects/service.ts.
          (data as { phaseId?: string | null }).phaseId = null;
        }
        // Task nunca teve fase e segue "Sem fase" → phaseId omitido (nada muda).
        await updateTask.mutateAsync({ projectId, taskId: task.id, data });
      }
      onClose();
    } catch {
      // hooks já exibem toast de erro
    }
  };

  const handleRemove = async () => {
    if (!task) return;
    try {
      await removeTask.mutateAsync({ projectId, taskId: task.id });
      setDeleteOpen(false);
      onClose();
    } catch {
      setDeleteOpen(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="xl">
      {/* F74: título 18px como prop tipada do ModalHeader */}
      <ModalHeader title={titleForHeader} onClose={onClose} />

      <ModalContent className="max-h-[70vh] overflow-y-auto">
        <div className="space-y-4" data-testid="project-ws-task-modal">
          {/* Título (create) / Ticket (link) / read-only (edit) */}
          {mode === 'create' && (
            <FormField label="Título da tarefa" required>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex: Levantamento de requisitos"
                data-testid="project-ws-task-title"
              />
            </FormField>
          )}

          {mode === 'link' && (
            <FormField label="Ticket existente" required description="Do pipeline do projeto, ainda não vinculado">
              <div className="space-y-2">
                <Input
                  icon="search"
                  value={ticketSearch}
                  onChange={(e) => setTicketSearch(e.target.value)}
                  placeholder="Buscar por número ou título"
                  data-testid="project-ws-task-ticket-search"
                />
                <Select
                  options={ticketOptions}
                  value={ticketId}
                  onChange={setTicketId}
                  placeholder={ticketOptions.length === 0 ? 'Nenhum ticket disponível' : 'Selecione o ticket'}
                  data-testid="project-ws-task-ticket"
                />
              </div>
            </FormField>
          )}

          {mode === 'edit' && task && (
            <div className="rounded-lg border px-3 py-2" style={{ borderColor: '#e5e7eb', backgroundColor: '#f8fafc' }}>
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono" style={{ color: '#64748b' }}>
                  #{task.ticket?.ticketNumber ?? '—'}
                </span>
                <span className="text-sm font-medium" style={{ color: '#1e293b' }}>
                  {task.ticket?.title ?? 'Tarefa'}
                </span>
              </div>
              <p className="text-xs mt-0.5" style={{ color: '#94a3b8' }}>
                O título é editado pelo ticket no helpdesk.
              </p>
            </div>
          )}

          {/* Fase + Responsável (create) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select
              label="Fase"
              options={phaseOptions}
              value={phaseId}
              onChange={setPhaseId}
              data-testid="project-ws-task-phase"
            />
            {mode === 'create' && (
              <Select
                label="Responsável"
                options={userOptions}
                value={assigneeId}
                onChange={setAssigneeId}
                data-testid="project-ws-task-assignee"
              />
            )}
          </div>

          {/* Datas + Marco */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <FormField label="Início">
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStart(e.target.value)}
                data-testid="project-ws-task-start"
              />
            </FormField>
            <FormField label="Fim">
              <Input
                type="date"
                value={isMilestone ? startDate : endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={isMilestone}
                data-testid="project-ws-task-end"
              />
            </FormField>
            <div className="flex items-end pb-1">
              <Checkbox
                label="Marco (data única)"
                checked={isMilestone}
                onChange={toggleMilestone}
                data-testid="project-ws-task-milestone"
              />
            </div>
          </div>

          {/* Dependências */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium" style={{ color: '#1e293b' }}>
                Dependências (Finish-to-Start)
              </label>
              <button
                type="button"
                onClick={addDep}
                className="flex items-center gap-1 text-xs font-medium transition-opacity hover:opacity-80"
                style={{ color: '#8b5cf6' }}
                data-testid="project-ws-task-dep-add"
              >
                <Icon name="plus" className="w-3.5 h-3.5" />
                Adicionar
              </button>
            </div>

            {deps.length === 0 ? (
              <p className="text-xs py-1" style={{ color: '#94a3b8' }}>
                Sem dependências. A tarefa pode começar a qualquer momento.
              </p>
            ) : (
              <div className="space-y-2">
                {deps.map((dep, index) => (
                  <div key={index} className="flex items-center gap-2" data-testid={`project-ws-task-dep-${index}`}>
                    <div className="flex-1">
                      <Select
                        options={[{ value: '', label: 'Selecione a tarefa' }, ...depOptions]}
                        value={dep.taskId}
                        onChange={(v) => updateDep(index, { taskId: v })}
                        data-testid={`project-ws-task-dep-task-${index}`}
                      />
                    </div>
                    <div className="w-44">
                      <Select
                        options={MODE_OPTIONS}
                        value={dep.mode}
                        onChange={(v) => updateDep(index, { mode: v as DependencyMode })}
                        data-testid={`project-ws-task-dep-mode-${index}`}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeDep(index)}
                      className="p-1.5 rounded-lg transition-colors flex-shrink-0"
                      style={{ color: '#64748b' }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f1f5f9')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                      title="Remover dependência"
                      data-testid={`project-ws-task-dep-remove-${index}`}
                    >
                      <Icon name="x" className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {pendingPredecessors.length > 0 && (
              <div
                className="mt-3 rounded-lg border p-3 flex gap-2"
                style={{ backgroundColor: '#fffbeb', borderColor: '#fde68a' }}
                data-testid="project-ws-task-warnbox"
              >
                <Icon name="alert-triangle" className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#b45309' }} />
                <p className="text-xs" style={{ color: '#92400e' }}>
                  {pendingPredecessors.length === 1 ? 'A predecessora ' : 'As predecessoras '}
                  <strong>{pendingPredecessors.map((t) => t.ticket?.title ?? 'Tarefa').join(', ')}</strong>{' '}
                  {pendingPredecessors.length === 1 ? 'ainda não foi concluída' : 'ainda não foram concluídas'}. A
                  conclusão do ticket será avisada/bloqueada conforme o modo.
                </p>
              </div>
            )}
          </div>
        </div>
      </ModalContent>

      <ModalFooter>
        {mode === 'edit' && (
          <button
            type="button"
            onClick={() => setDeleteOpen(true)}
            className="mr-auto text-sm font-medium transition-opacity hover:opacity-80"
            style={{ color: '#dc2626' }}
            data-testid="project-ws-task-remove"
          >
            Remover do projeto
          </button>
        )}
        <Button variant="secondary" onClick={onClose} data-testid="project-ws-task-cancel">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          loading={isSaving}
          icon="check"
          data-testid="project-ws-task-save"
        >
          {mode === 'create' ? 'Criar tarefa' : mode === 'link' ? 'Vincular' : 'Salvar'}
        </Button>
      </ModalFooter>

      <DeleteConfirmationModal
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleRemove}
        title="Remover do projeto"
        message={`Remover "${task?.ticket?.title ?? 'esta tarefa'}" do projeto?`}
        warningMessage="O ticket permanece no helpdesk — apenas o vínculo com o projeto é desfeito."
        confirmButtonText="Remover do projeto"
        isLoading={removeTask.isPending}
      />
    </Modal>
  );
};

TaskModal.displayName = 'TaskModal';
